import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Check, ChevronDown, ChevronUp, Clipboard, Download, FileJson, GitCompare, History, KeyRound, RefreshCw, RotateCcw, ShieldCheck, XCircle } from 'lucide-react'
import { apiGet, apiPost } from '../../lib/api'
import { formatConfigurationExport } from '../../lib/configurationExport'
import { fileArtifactPresentation, secretBindingLabel } from '../../lib/workspace'

export interface ConfigurationReview {
  id: string
  action: 'approved' | 'rejected' | 'changes_requested' | 'deployed'
  comment?: string | null
  created_by?: string | null
  created_at: string
}

export interface ConfigurationArtifactVersion {
  id: string
  version: number
  state: 'draft' | 'saved' | 'approved' | 'deployed' | 'failed'
  config_json: Record<string, unknown>
  persisted_config_json?: Record<string, unknown> | null
  source_message_id?: string | null
  source_tool_name?: string | null
  validation_summary?: Record<string, unknown> | null
  latest_review?: ConfigurationReview | null
  review_history?: ConfigurationReview[]
  created_at: string
}

export interface ConfigurationArtifact {
  id: string
  project_id: string
  connection_id?: string | null
  conversation_id?: string | null
  connection_name?: string | null
  connector_family: string
  state: 'draft' | 'saved' | 'approved' | 'deployed' | 'failed'
  current_version: number
  validation_summary?: Record<string, unknown> | null
  pending_approval: boolean
  created_at: string
  updated_at: string
  version: ConfigurationArtifactVersion | null
}

interface ArtifactDiff {
  version: number
  counts: { added: number; modified: number; removed: number }
  potentially_destructive: boolean
  changes: Array<{
    path: string
    change: 'added' | 'modified' | 'removed'
    before: unknown
    after: unknown
    potentially_destructive: boolean
  }>
}

interface SecretBinding {
  field_path: string
  secret_names: string[]
  mechanism: 'direct' | 'base64' | 'external' | 'unnamed'
  purpose: string
  consumer: string
  status: 'bound' | 'missing' | 'external' | 'unnamed'
  missing_secret_names: string[]
}

interface SecretBindingResponse {
  version: number
  bindings: SecretBinding[]
  counts: Record<SecretBinding['status'], number>
  all_required_bound: boolean
}

interface ArtifactVersionHistoryItem {
  id: string
  version: number
  state: string
  validation_summary?: { status?: string; counts?: Record<string, number> } | null
  source_message_id?: string | null
  source_tool_name?: string | null
  created_by_name: string
  created_at: string
  is_current: boolean
  evidence: { added: number; modified: number; removed: number; destructive: number }
}

function displayValue(value: unknown) {
  if (value === null || value === undefined) return '—'
  return typeof value === 'string' ? value : JSON.stringify(value)
}

export default function ConfigurationArtifactPanel({
  artifact, loading, onRefresh,
}: {
  artifact: ConfigurationArtifact | null
  loading: boolean
  onRefresh: () => void
}) {
  const [diff, setDiff] = useState<ArtifactDiff | null>(null)
  const [copied, setCopied] = useState(false)
  const [reviewComment, setReviewComment] = useState('')
  const [reviewSubmitting, setReviewSubmitting] = useState(false)
  const [reviewError, setReviewError] = useState('')
  const [secretBindings, setSecretBindings] = useState<SecretBindingResponse | null>(null)
  const [secretBindingsLoading, setSecretBindingsLoading] = useState(false)
  const [secretBindingsError, setSecretBindingsError] = useState('')
  const [versionHistory, setVersionHistory] = useState<ArtifactVersionHistoryItem[]>([])
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false)
  const [versionHistoryLoading, setVersionHistoryLoading] = useState(false)
  const [versionHistoryError, setVersionHistoryError] = useState('')
  const [downloadingVersion, setDownloadingVersion] = useState<number | null>(null)
  const [rollbackTarget, setRollbackTarget] = useState<ArtifactVersionHistoryItem | null>(null)
  const [rollbackSubmitting, setRollbackSubmitting] = useState(false)
  const staleVersionRefreshRef = useRef<string | null>(null)
  const configText = useMemo(
    () => artifact?.version ? JSON.stringify(artifact.version.config_json, null, 2) : '',
    [artifact],
  )
  const isFileArtifact = artifact?.connector_family?.toUpperCase() === 'FILE'
  const fileDetails = useMemo(
    () => isFileArtifact && artifact?.version ? fileArtifactPresentation(artifact.version.config_json) : null,
    [artifact, isFileArtifact],
  )
  const artifactText = fileDetails?.schemaContent || configText

  useEffect(() => {
    let cancelled = false
    setDiff(null)
    setSecretBindings(null)
    setSecretBindingsError('')
    setVersionHistory([])
    setVersionHistoryError('')
    if (!artifact?.version) return
    setSecretBindingsLoading(true)
    apiGet(`/api/artifacts/${artifact.id}/diff?version=${artifact.version.version}`)
      .then(value => { if (!cancelled) setDiff(value) })
      .catch(() => { if (!cancelled) setDiff(null) })
    apiGet(`/api/artifacts/${artifact.id}/secret-bindings?version=${artifact.version.version}`)
      .then(value => { if (!cancelled) setSecretBindings(value) })
      .catch((error: any) => {
        if (!cancelled) setSecretBindingsError(error?.message || 'Could not load secret bindings')
      })
      .finally(() => { if (!cancelled) setSecretBindingsLoading(false) })
    setVersionHistoryLoading(true)
    apiGet(`/api/artifacts/${artifact.id}/versions`)
      .then(value => {
        if (cancelled) return
        const items = Array.isArray(value?.items) ? value.items : []
        setVersionHistory(items)
        const currentVersion = items.find((item: ArtifactVersionHistoryItem) => item.is_current)?.version
        if (currentVersion && currentVersion !== artifact.version?.version) {
          const mismatchKey = `${artifact.id}:${artifact.version?.version || 0}->${currentVersion}`
          if (staleVersionRefreshRef.current !== mismatchKey) {
            staleVersionRefreshRef.current = mismatchKey
            onRefresh()
          }
        } else {
          staleVersionRefreshRef.current = null
        }
      })
      .catch((error: any) => { if (!cancelled) setVersionHistoryError(error?.message || 'Could not load version history') })
      .finally(() => { if (!cancelled) setVersionHistoryLoading(false) })
    return () => { cancelled = true }
  }, [artifact, onRefresh])

  const copyConfig = () => {
    navigator.clipboard.writeText(artifactText).then(() => {
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    })
  }

  const saveConfigDownload = (config: Record<string, unknown>, version: number) => {
    if (!artifact) return
    const selectedFileDetails = isFileArtifact ? fileArtifactPresentation(config) : null
    const downloadText = selectedFileDetails?.schemaContent || formatConfigurationExport(config)
    const blob = new Blob([downloadText], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = selectedFileDetails?.schemaFileName || `${artifact.connection_name || 'connector'}-v${version}.txt`
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const downloadConfig = () => {
    if (!artifact?.version) return
    saveConfigDownload(artifact.version.config_json, artifact.version.version)
  }

  const downloadHistoricalVersion = async (item: ArtifactVersionHistoryItem) => {
    if (!artifact?.version || downloadingVersion !== null) return
    if (item.version === artifact.version.version) {
      downloadConfig()
      return
    }
    setDownloadingVersion(item.version)
    setVersionHistoryError('')
    try {
      const selected = await apiGet(`/api/artifacts/${artifact.id}/versions/${item.version}`)
      saveConfigDownload(selected.config_json || {}, item.version)
    } catch (error: any) {
      setVersionHistoryError(error?.message || `Could not download version ${item.version}`)
    } finally {
      setDownloadingVersion(null)
    }
  }

  const submitReview = async (action: Exclude<ConfigurationReview['action'], 'deployed'>) => {
    if (!artifact?.version || reviewSubmitting) return
    if (action !== 'approved' && !reviewComment.trim()) {
      setReviewError('Add a comment explaining what should change.')
      return
    }
    setReviewSubmitting(true)
    setReviewError('')
    try {
      await apiPost(`/api/artifacts/${artifact.id}/versions/${artifact.version.version}/reviews`, {
        action,
        comment: reviewComment.trim() || null,
      })
      setReviewComment('')
      onRefresh()
    } catch (error: any) {
      setReviewError(error?.message || 'Review action failed')
    } finally {
      setReviewSubmitting(false)
    }
  }

  const createRollback = async () => {
    if (!artifact || !rollbackTarget || rollbackSubmitting) return
    setRollbackSubmitting(true)
    setVersionHistoryError('')
    try {
      await apiPost(`/api/artifacts/${artifact.id}/versions/${rollbackTarget.version}/rollback`, {})
      setRollbackTarget(null)
      onRefresh()
    } catch (error: any) {
      setVersionHistoryError(error?.message || 'Could not create rollback draft')
    } finally {
      setRollbackSubmitting(false)
    }
  }

  if (loading) return <div className="artifact-empty"><RefreshCw size={18} className="spin" /><p>Loading configuration artifact…</p></div>
  if (!artifact?.version) return (
    <div className="artifact-empty">
      <FileJson size={22} />
      <h3>No configuration artifact yet</h3>
      <p>Generated or deployed connector configuration will appear here as a durable version.</p>
      <button type="button" onClick={onRefresh}><RefreshCw size={12} /> Refresh</button>
    </div>
  )

  const validationStatus = String(
    artifact.version.validation_summary?.status || artifact.validation_summary?.status || 'not run'
  )
  const latestReview = artifact.version.latest_review
  const canApprove = validationStatus.toLowerCase() === 'passed'
  const isDeployed = artifact.version.state === 'deployed'
  const reviewState = isDeployed ? 'deployed' : latestReview?.action || 'pending'

  return (
    <div className="configuration-artifact-panel">
      <div className="artifact-heading">
        <div>
          <span>{fileDetails ? 'Schema import artifact' : 'Configuration artifact'}</span>
          <h3>{artifact.connection_name || 'Unnamed connection'}</h3>
        </div>
        <button type="button" onClick={onRefresh} aria-label="Refresh configuration artifact"><RefreshCw size={13} /></button>
      </div>

      <div className="artifact-meta-grid">
        <div><span>Family</span><strong>{artifact.connector_family}</strong></div>
        <div><span>State</span><strong className={`artifact-state ${artifact.state}`}>{artifact.state}</strong></div>
        <div><span>Version</span><strong>v{artifact.version.version}</strong></div>
        <div><span>Validation</span><strong>{validationStatus}</strong></div>
      </div>

      <section className="artifact-section artifact-version-history">
        <button className="artifact-version-toggle" type="button" onClick={() => setVersionHistoryOpen(value => !value)}>
          <span><History size={12} /> Version history</span>
          <span>{versionHistory.length} versions {versionHistoryOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}</span>
        </button>
        {versionHistoryOpen && (
          <div className="artifact-version-list">
            <p className="artifact-version-safety">Rollback always creates a new draft from historical content. It never deploys or overwrites history directly.</p>
            {versionHistoryLoading && <p className="artifact-muted"><RefreshCw size={11} className="spin" /> Loading versions…</p>}
            {versionHistoryError && <p className="artifact-review-error">{versionHistoryError}</p>}
            {versionHistory.map(item => (
              <div className={`artifact-version-row ${item.is_current ? 'current' : ''}`} key={item.id}>
                <div className="artifact-version-number"><strong>v{item.version}</strong><span>{item.is_current ? 'Current' : item.state}</span></div>
                <div className="artifact-version-info">
                  <strong>{item.source_tool_name ? item.source_tool_name.replaceAll('_', ' ') : 'Configuration version'}</strong>
                  <span>{new Date(item.created_at).toLocaleString()} · {item.created_by_name}</span>
                  <small>+{item.evidence.added} · ~{item.evidence.modified} · −{item.evidence.removed}{item.evidence.destructive ? ` · ${item.evidence.destructive} destructive` : ''}</small>
                </div>
                <div className="artifact-version-actions">
                  <button type="button" disabled={downloadingVersion !== null} onClick={() => downloadHistoricalVersion(item)}>
                    <Download size={11} /> {downloadingVersion === item.version ? 'Downloading…' : 'Download'}
                  </button>
                  {!item.is_current && <button type="button" onClick={() => setRollbackTarget(item)}><RotateCcw size={11} /> Restore</button>}
                </div>
              </div>
            ))}
            {rollbackTarget && (
              <div className="artifact-rollback-confirm">
                <strong>Create a rollback draft from v{rollbackTarget.version}?</strong>
                <p>The current version remains immutable. A new version will be created, validated locally, and sent through approval before any deployment.</p>
                <div><button type="button" onClick={() => setRollbackTarget(null)}>Cancel</button><button type="button" className="confirm" disabled={rollbackSubmitting} onClick={createRollback}>{rollbackSubmitting ? 'Creating…' : `Create draft from v${rollbackTarget.version}`}</button></div>
              </div>
            )}
          </div>
        )}
      </section>

      <section className="artifact-section artifact-secret-bindings">
        <div className="artifact-section-head">
          <h4><KeyRound size={12} /> Secret bindings</h4>
          {secretBindings && (
            <span className={secretBindings.all_required_bound ? 'artifact-binding-ready' : 'artifact-binding-attention'}>
              {secretBindings.counts.bound || 0} bound
              {(secretBindings.counts.missing || secretBindings.counts.unnamed) ? ` · ${(secretBindings.counts.missing || 0) + (secretBindings.counts.unnamed || 0)} attention` : ''}
            </span>
          )}
        </div>
        {secretBindingsLoading ? (
          <p className="artifact-muted"><RefreshCw size={11} className="spin" /> Loading value-free binding manifest…</p>
        ) : secretBindingsError ? (
          <p className="artifact-review-error">{secretBindingsError}</p>
        ) : !secretBindings?.bindings.length ? (
          <p className="artifact-muted">No named secret references are used by this version.</p>
        ) : (
          <div className="artifact-binding-list">
            {secretBindings.bindings.map(binding => (
              <div className={`artifact-binding-row ${binding.status}`} key={`${binding.field_path}-${binding.mechanism}-${binding.secret_names.join('-')}`}>
                <div className="artifact-binding-status">
                  {binding.status === 'bound' ? <ShieldCheck size={13} /> : <AlertTriangle size={13} />}
                </div>
                <div className="artifact-binding-main">
                  <strong>{binding.purpose}</strong>
                  <code>{binding.field_path}</code>
                  <span>{binding.consumer.replaceAll('_', ' ')}</span>
                </div>
                <div className="artifact-binding-name">
                  <code>{secretBindingLabel(binding)}</code>
                  <span>{binding.status === 'bound' ? 'Available' : binding.status === 'missing' ? `Missing: ${binding.missing_secret_names.join(', ')}` : binding.status === 'external' ? 'Resolved outside project vault' : 'Explicit binding required'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="artifact-binding-safety">Secret values remain encrypted and server-side. This view contains names, purposes, and availability only.</p>
      </section>

      <section className={`artifact-review artifact-review-${reviewState}`}>
        <div className="artifact-review-status">
          {isDeployed || latestReview?.action === 'approved' ? <ShieldCheck size={15} /> : latestReview?.action === 'rejected' ? <XCircle size={15} /> : <AlertTriangle size={15} />}
          <div>
            <strong>{isDeployed ? (fileDetails ? 'Uploaded' : 'Deployed') : latestReview?.action === 'approved' ? (fileDetails ? 'Approved for upload' : 'Approved for deployment') : latestReview?.action === 'rejected' ? 'Rejected' : latestReview?.action === 'changes_requested' ? 'Changes requested' : 'Approval required'}</strong>
            <span>{isDeployed
              ? `Version ${artifact.version.version} is the ${fileDetails ? 'uploaded SAV schema' : 'deployed configuration'}.`
              : latestReview
                ? `Decision recorded ${new Date(latestReview.created_at).toLocaleString()}`
                : `Review validated version ${artifact.version.version} before ${fileDetails ? 'upload' : 'deployment'}.`}
            </span>
          </div>
        </div>
        {latestReview?.comment && <p className="artifact-review-comment">{latestReview.comment}</p>}
        {!isDeployed && (
          <>
            <textarea
              value={reviewComment}
              onChange={event => setReviewComment(event.target.value)}
              placeholder="Optional approval note; required for rejection or requested changes"
              aria-label="Configuration review comment"
              rows={2}
            />
            {reviewError && <p className="artifact-review-error">{reviewError}</p>}
            <div className="artifact-review-actions">
              <button type="button" className="approve" disabled={!canApprove || reviewSubmitting} onClick={() => submitReview('approved')}>
                <ShieldCheck size={12} /> Approve v{artifact.version.version}
              </button>
              <button type="button" disabled={reviewSubmitting} onClick={() => submitReview('changes_requested')}>
                <RotateCcw size={12} /> Request changes
              </button>
              <button type="button" className="reject" disabled={reviewSubmitting} onClick={() => submitReview('rejected')}>
                <XCircle size={12} /> Reject
              </button>
            </div>
            {!canApprove && <span className="artifact-review-hint">Approval unlocks only after validation passes.</span>}
          </>
        )}
      </section>

      <section className="artifact-section">
        <div className="artifact-section-head">
          <h4>{fileDetails ? 'Current SAV schema' : 'Current generated configuration'}</h4>
          <div>
            <button type="button" onClick={copyConfig}>{copied ? <Check size={11} /> : <Clipboard size={11} />}{copied ? 'Copied' : 'Copy'}</button>
            <button type="button" onClick={downloadConfig}><Download size={11} />Download</button>
          </div>
        </div>
        {fileDetails && (
          <div className="artifact-file-meta">
            <div><span>SAV file</span><strong>{fileDetails.schemaFileName}</strong></div>
            <div><span>Source file</span><strong>{fileDetails.sourceFileName || 'Not set'}</strong></div>
            <div><span>Delimiter</span><strong>{fileDetails.delimiter === '\t' ? 'Tab' : fileDetails.delimiter}</strong></div>
            <div><span>Columns</span><strong>{fileDetails.mappings.length}</strong></div>
          </div>
        )}
        <pre className="artifact-config-json">{artifactText}</pre>
      </section>

      {fileDetails && (
        <section className="artifact-section artifact-file-mapping">
          <div className="artifact-section-head"><h4>Positional feed mapping</h4></div>
          <div className="artifact-file-mapping-head"><span>#</span><span>Source header</span><span>Saviynt attribute</span></div>
          {fileDetails.mappings.map(mapping => (
            <div className="artifact-file-mapping-row" key={`${mapping.position}-${mapping.source}-${mapping.target}`}>
              <span>{mapping.position}</span><code>{mapping.source}</code><code>{mapping.target}</code>
            </div>
          ))}
        </section>
      )}

      <section className="artifact-section artifact-diff">
        <div className="artifact-section-head"><h4><GitCompare size={12} /> Changes from persisted {fileDetails ? 'schema' : 'config'}</h4></div>
        {!diff ? <p className="artifact-muted">Loading structured diff…</p> : diff.changes.length === 0 ? (
          <p className="artifact-no-changes"><Check size={12} /> This version matches the persisted configuration.</p>
        ) : (
          <>
            <div className="artifact-diff-counts">
              <span className="added">+{diff.counts.added} added</span>
              <span className="modified">~{diff.counts.modified} modified</span>
              <span className="removed">−{diff.counts.removed} removed</span>
            </div>
            {diff.potentially_destructive && <div className="artifact-warning"><AlertTriangle size={12} /> Review potentially destructive changes.</div>}
            <div className="artifact-change-list">
              {diff.changes.slice(0, 100).map(change => (
                <div key={`${change.change}-${change.path}`} className={`artifact-change ${change.change}`}>
                  <strong>{change.path}</strong>
                  <span>{change.change}</span>
                  {change.change !== 'added' && <code>− {displayValue(change.before)}</code>}
                  {change.change !== 'removed' && <code>+ {displayValue(change.after)}</code>}
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {Boolean(artifact.version.review_history?.length) && (
        <section className="artifact-section artifact-review-history">
          <h4>Review history</h4>
          {artifact.version.review_history!.map(review => (
            <div key={review.id}>
              <strong>{review.action.replaceAll('_', ' ')}</strong>
              <span>{new Date(review.created_at).toLocaleString()}</span>
              {review.comment && <p>{review.comment}</p>}
            </div>
          ))}
        </section>
      )}

      <div className="artifact-provenance">
        <span>Updated {new Date(artifact.updated_at).toLocaleString()}</span>
        {artifact.version.source_message_id && <a href={`#message-${artifact.version.source_message_id}`}>View source message</a>}
        {artifact.version.source_tool_name && <span>via {artifact.version.source_tool_name.replaceAll('_', ' ')}</span>}
      </div>
    </div>
  )
}
